
















































































































import { Vue, Component, Prop, Ref } from "vue-property-decorator";
import {
  ComBox,
  ComBoxDeviceState,
  DryingPauseType,
  getComBoxOffsetToUtc,
  getComBoxState,
  isHyDryMini,
  isLidOpen,
} from "@/data/models/ComBox";
import {
  createPowerOnRequest,
  createGetConfigsRequest,
  createPowerOffRequest,
  createStartComBoxRequest,
  createPauseComBoxCommand,
  createTestRelaysCommando,
  createResumeComBoxCommand,
  createTerminateComBoxDryRunCommand,
} from "@/utils/combox/commands/ComBoxCommands";

import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import ConfirmMeterReadingComponent from "@/components/combox/ConfirmMeterReading.vue";
import CreateControlConfigComponent from "@/components/combox/CreateControlConfig.vue";
import PauseComBoxComponent from "@/components/combox/PauseComBox.vue";
import TerminateDryRunComponent from "@/components/combox/TerminateDryRun.vue";
import { IresDeviceStatusSource } from "@/data/models/IresDevice";
import {
  getActiveUser,
} from "@/data/repository/UserRepository";
import { ProgramChain } from "@/data/models/ProgramChain";
import TestRelaysComponent from "./TestRelays.vue";
import { 
  viewPowerOnButtonAccess,
  viewTestRelaysButtonAccess,
  iotCommandsAccess,
  controlComBoxViaIotAccess 
} from "@/permissions/UserPermissions"

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "confirm-meter-reading": ConfirmMeterReadingComponent,
    "create-control-config": CreateControlConfigComponent,
    "pause-com-box": PauseComBoxComponent,
    "test-relays": TestRelaysComponent,
    "terminate-dry-run": TerminateDryRunComponent,
  },
})
export default class ComBoxControlComponent extends Vue {
  @Prop() comBox!: ComBox;
  @Prop() selectedProgramChain!: ProgramChain;
  @Prop({ default: false }) showSetIresDeviceOfflineButton!: boolean;
  @Prop({ default: () => new Map() }) sensorLegendMetadata!: Map<number, { serialNumber?: string }>;

  @Ref("confirmComBoxAction")
  confirmationDialog!: ConfirmationDialogComponent;

  @Ref("confirmMeterReading")
  confirmMeterReading!: ConfirmMeterReadingComponent;

  @Ref("createControlConfig")
  createControlConfig!: CreateControlConfigComponent;

  @Ref("terminateDryRun")
  terminateDryRun!: TerminateDryRunComponent;

  @Ref("pauseComBox")
  pauseComBox!: PauseComBoxComponent;

  @Ref("testRelays")
  testRelays!: TestRelaysComponent;

  showingSuccessSnackbar = false;
  successSnackbarTimeout = 5000;
  successSnackbarText = "";

  /** Phones: the command buttons are small, the size of the view buttons below (#2610011). */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  get comBoxStatus() {
    return this.comBox.comBoxStatus;
  }

  get isComBoxOnline() {
    return this.comBoxStatus?.online ?? false;
  }

  onSetComBoxOffline() {
    this.$emit("setIresDeviceOffline");
  }

  isComBoxInState(...comBoxDeviceStates: ComBoxDeviceState[]) {
    const currentState = getComBoxState(this.comBox);

    return comBoxDeviceStates.some((state) => state === currentState);
  }

  /** Hide all control buttons for minis in DRY_RUN_ENDED or SUSPENDED state. */
  get hideAllButtonsForMini() {
    return isHyDryMini(this.comBox) && this.isComBoxInState(
      ComBoxDeviceState.DRY_RUN_ENDED,
      ComBoxDeviceState.SUSPENDED
    );
  }

  get confirmationMessage() {
    return (
      this.$t(
        this.isComBoxOnline
          ? "panelControl.executingCommandImmediatly"
          : "panelControl.executingCommandNextIotConnection"
      ).toString() +
      " " +
      this.$t("common.areYouSure").toString()
    );
  }

  async askForComBoxActionConfirmation(title: string) {
    const acceptMessge = this.$t("common.execute").toString();
    return await this.confirmationDialog.show({
      title:
        this.$t("comBox.formatComBox", [this.comBox.serialNo]).toString() +
        " " +
        this.$t(title).toString(),
      message: this.confirmationMessage,
      acceptMessage: acceptMessge,
    });
  }

  get activeUser() {
    return getActiveUser();
  }

  get showPowerOnButton() {
    const hasUserRightsToPowerOn =
      viewPowerOnButtonAccess(this.activeUser);

    return (
      hasUserRightsToPowerOn &&
      controlComBoxViaIotAccess(this.activeUser) &&
      this.isComBoxInState(ComBoxDeviceState.STANDBY)
    );
  }

  get showPowerOffButton() {
    return this.isComBoxInState(
      ComBoxDeviceState.IDLE,
      ComBoxDeviceState.DRY_RUN_ENDED
    );
  }

  get showStartButton() {
    return this.isComBoxInState(
      ComBoxDeviceState.IDLE,
      ComBoxDeviceState.DRY_RUN_ENDED
    );
  }

  get showChangeButton() {
    return this.isComBoxInState(ComBoxDeviceState.ACTIVE);
  }

  get showPauseButton() {
    return this.isComBoxInState(ComBoxDeviceState.ACTIVE);
  }

  get showTestRelaysButton() {
    return (
      viewTestRelaysButtonAccess(this.activeUser) &&
      !isHyDryMini(this.comBox) &&
      !this.isComBoxInState(
        ComBoxDeviceState.POWER_FAIL,
        ComBoxDeviceState.TRANSITION
      )
    );
  }

  get showTerminateDryRunButton() {
    return this.isComBoxInState(
      ComBoxDeviceState.ACTIVE,
      ComBoxDeviceState.WAITING
    );
  }

  get showResumeButton() {
    return this.isComBoxInState(
      ComBoxDeviceState.SUSPENDED,
      ComBoxDeviceState.WAITING
    );
  }

  get areIotCommandsDisabled() {
    if (iotCommandsAccess(this.activeUser)) {
      //IRES client is always allowed to interact with combox
      return false;
    }

    return (
      isLidOpen(this.comBox) ||
      this.comBox.comBoxStatus.source === IresDeviceStatusSource.USB_OR_BLE
    );
  }

  get pauseDisabled() {
    return (
      this.comBox.comBoxStatus.dryingPauseType !== DryingPauseType.INACTIVE
    );
  }

  async onTerminateDryRun() {
    const terminateDryRun = await this.terminateDryRun.show();

    if (terminateDryRun) {
      await createTerminateComBoxDryRunCommand(this.comBox.serialNo);
    }
  }

  async onResumeComBox() {
    if (
      await this.askForComBoxActionConfirmation("comBoxDetail.control.resume")
    ) {
      await createResumeComBoxCommand(this.comBox.serialNo);
    }
  }

  async onTestRelays() {
    const relayTestConfig = await this.testRelays.show(this.comBox.serialNo);

    if (relayTestConfig) {
      await createTestRelaysCommando(
        this.comBox.serialNo,
        relayTestConfig.numCycles,
        relayTestConfig.onOffDuration,
        relayTestConfig.relay1Active,
        relayTestConfig.relay2Active
      );
    }
  }

  async onPauseComBox() {
    const pauseDurationInMin = await this.pauseComBox.show(
      this.comBox.serialNo,
      this.confirmationMessage
    );

    if (pauseDurationInMin) {
      await createPauseComBoxCommand(
        this.comBox.serialNo,
        pauseDurationInMin,
        true
      );
    }
  }

  async onChangeDryRun() {
    const controlConfig = await this.createControlConfig.show(
      false,
      this.confirmationMessage
    );

    if (controlConfig) {
      await createStartComBoxRequest(
        this.comBox.serialNo,
        getComBoxOffsetToUtc(this.comBox),
        controlConfig,
        false,
        this.comBox.comBoxFirmwareVersion
      );
    }
  }

  async onStartDryRun() {
    const controlConfig = await this.createControlConfig.show(
      true,
      this.confirmationMessage
    );

    if (controlConfig) {
      await createStartComBoxRequest(
        this.comBox.serialNo,
        getComBoxOffsetToUtc(this.comBox),
        controlConfig,
        true,
        this.comBox.comBoxFirmwareVersion
      );
    }
  }

  async onPowerOffComBox() {
    if (
      await this.askForComBoxActionConfirmation("comBoxDetail.control.powerOff")
    ) {
      await createPowerOffRequest(this.comBox.serialNo);
    }
  }

  async onGetConfigs() {
    await createGetConfigsRequest(this.comBox.serialNo);
  }

  async onPowerOnComBox() {
    const powerOnMessageKey = isHyDryMini(this.comBox)
      ? "comBoxDetail.control.confirmPowerOn.messageMini"
      : "comBoxDetail.control.confirmPowerOn.messageBox";

    const didConfirmPowerOn = await this.confirmationDialog.show({
      title: this.$t("comBoxDetail.control.confirmPowerOn.title").toString(),
      message: this.$t(powerOnMessageKey).toString(),
      acceptMessage: this.$t("comBoxDetail.control.powerOn").toString(),
    });

    if (!didConfirmPowerOn) {
      return;
    }

    const meterReading = this.comBox.startUpEnergyInKwh;

    const didConfirmMeterReading = await this.confirmMeterReading.show(
      meterReading,
      this.$t("comBoxDetail.control.formatPowerOnTitle", [
        this.comBox.serialNo,
      ]).toString()
    );

    if (didConfirmMeterReading) {
      await createPowerOnRequest(this.comBox.serialNo, meterReading);
    }
  }
}
